import type { AxiosError, AxiosInstance } from 'axios';

import { generateRequestId } from '@/lib/requestId';
import type { ApiConfig } from '@/services/api/configTypes';

// Attaches a per-request `X-Request-Id` (32-char hex, matching the backend's
// x-dokka-request-id format) to every outgoing request on the given client,
// for end-to-end log correlation. Runs after executeRequest has assembled its
// headers, so it only adds the id on top — no conflict. Applied per-instance
// (currently pay-only) rather than baked into defineApi, so the AP client
// stays untouched.
export const attachRequestIdInterceptor = (client: AxiosInstance): void => {
  client.interceptors.request.use((config) => {
    config.headers.set('X-Request-Id', generateRequestId());
    return config;
  });
};

// Client-agnostic response interceptor. Identical logic to the legacy inline
// interceptor (client.ts), but parameterised by a config getter so multiple
// axios instances (standard + payment) can each attach it against their own
// config. Always rejects — the callbacks are side-effects, never suppress.
export const attachResponseInterceptor = (client: AxiosInstance, getCfg: () => ApiConfig): void => {
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const cfg = getCfg();
      const status = error.response?.status;
      const code = status?.toString();

      if (code === process.env.REACT_APP_UNAUTHORIZED_CODE) cfg.onAuthFailure(error);
      if (code === process.env.REACT_APP_TRIAL_EXPIRED_CODE) cfg.onTrialExpired(error);
      if (code === process.env.REACT_APP_ACCESS_DENIED_CODE) cfg.onAccessDenied(error);
      if (status === 426) cfg.onUpgradeRequired(error);

      return Promise.reject(error);
    },
  );
};
